import { useEffect, useState } from 'react';
import { PAISES } from '../dados/paises';
import { UFS } from '../dados/ufs-br';
import { Campo, LinhaCampos } from './ui';

/**
 * País, estado e cidade — encadeados.
 *
 * Texto livre aqui produz "SP", "São Paulo", "Sao Paulo" e "sp" como quatro
 * lugares diferentes, e nenhum filtro ou relatório volta a funcionar depois.
 *
 * Fora do Brasil os campos viram texto livre, e isso é intencional: manter a
 * base de municípios de 193 países atualizada é um projeto por si só, e a
 * alternativa honesta é deixar quem cadastra escrever.
 */
export function CamposLocalidade({
  paisInicial = 'BR',
  estadoInicial = '',
  cidadeInicial = '',
}: {
  paisInicial?: string;
  estadoInicial?: string;
  cidadeInicial?: string;
}) {
  const [pais, setPais] = useState(paisInicial);
  const [uf, setUf] = useState(estadoInicial);
  const [cidade, setCidade] = useState(cidadeInicial);

  const [cidadesPorUf, setCidadesPorUf] = useState<Record<string, string[]> | null>(null);

  /**
   * A base de municípios passa de 80 KB e só serve ao Brasil. `import()`
   * dinâmico a mantém fora do pacote inicial: ela desce quando este formulário
   * aparece com o país BR, e não em toda visita ao sistema. As UFs não precisam
   * disso — são 27, e moram num arquivo próprio.
   */
  useEffect(() => {
    if (pais !== 'BR' || cidadesPorUf) return;

    let vivo = true;
    import('../dados/localidades-br').then((modulo) => {
      if (vivo) setCidadesPorUf(modulo.CIDADES_POR_UF);
    });
    return () => {
      vivo = false;
    };
  }, [pais, cidadesPorUf]);

  const brasil = pais === 'BR';
  const cidades = brasil && uf ? (cidadesPorUf?.[uf] ?? []) : [];

  function trocarPais(novo: string) {
    setPais(novo);
    // Estado e cidade pertencem ao país anterior. Mantê-los produziria
    // "Portugal / SP / Campinas" — um endereço que não existe.
    setUf('');
    setCidade('');
  }

  return (
    <>
      <LinhaCampos>
        <Campo
          label="País"
          name="country"
          obrigatorio
          valor={pais}
          aoMudar={trocarPais}
          opcoes={PAISES.map((p) => ({ valor: p.codigo, rotulo: p.nome }))}
        />
        {brasil ? (
          <Campo
            label="Estado"
            name="state"
            valor={uf}
            aoMudar={(v) => {
              setUf(v);
              setCidade('');
            }}
            opcoes={[
              { valor: '', rotulo: 'Selecione…' },
              ...UFS.map((u) => ({ valor: u.sigla, rotulo: `${u.nome} (${u.sigla})` })),
            ]}
          />
        ) : (
          <Campo
            label="Estado / Província"
            name="state"
            valor={uf}
            aoMudar={setUf}
            placeholder="Digite o nome"
          />
        )}
      </LinhaCampos>

      <LinhaCampos>
        {brasil ? (
          <Campo
            label="Cidade"
            name="city"
            valor={cidade}
            aoMudar={setCidade}
            opcoes={[
              {
                valor: '',
                rotulo: !uf ? 'Escolha o estado primeiro' : `Selecione… (${cidades.length})`,
              },
              ...cidades.map((c) => ({ valor: c, rotulo: c })),
            ]}
            ajuda={uf ? undefined : 'A lista de cidades depende do estado.'}
          />
        ) : (
          <Campo
            label="Cidade"
            name="city"
            valor={cidade}
            aoMudar={setCidade}
            placeholder="Digite o nome"
          />
        )}
        <div />
      </LinhaCampos>
    </>
  );
}
